import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError, type Portfolio, type PortfolioTemplate, type PortfolioUpdate, type RuntimeConfig, type SocialLink } from "../lib/api";
import { AIAssist } from "../components/AIAssist";
import { slugify as _slugify, cleanSlugInput as _cleanSlugInput } from "../lib/slug";

// Portfolio slugs are capped at 40 chars (shorter than galleries).
function slugify(s: string): string { return _slugify(s, 40); }
function cleanSlugInput(s: string): string { return _cleanSlugInput(s, 40); }

export function PortfolioPage() {
  const { t } = useTranslation();
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [templates, setTemplates] = useState<PortfolioTemplate[]>([]);
  const [runtimeCfg, setRuntimeCfg] = useState<RuntimeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [showClientArea, setShowClientArea] = useState(false);

  // Create-portfolio form state (when getMyPortfolio 404s).
  const [createSlug, setCreateSlug] = useState("");
  const [createSlugEdited, setCreateSlugEdited] = useState(false);
  const [createName, setCreateName] = useState("");
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [p, ts, cfg] = await Promise.all([
        api.getMyPortfolio().catch((err) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
        api.listPortfolioTemplates().catch(() => []),
        api.runtimeConfig().catch(() => null),
      ]);
      setPortfolio(p);
      setTemplates(ts);
      setRuntimeCfg(cfg);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const finalSlug = slugify(createSlug); // strip leading/trailing hyphens on submit
    if (!finalSlug || !createName) return;
    setCreating(true);
    setError(null);
    try {
      const p = await api.createPortfolio({
        slug: finalSlug,
        display_name: createName,
      });
      setPortfolio(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create");
    } finally {
      setCreating(false);
    }
  }

  function patch<K extends keyof Portfolio>(key: K, value: Portfolio[K]) {
    setPortfolio((p) => (p ? { ...p, [key]: value } : p));
  }

  function buildUpdate(p: Portfolio): PortfolioUpdate {
    return {
      display_name: p.DisplayName,
      tagline: p.Tagline,
      headline: p.Headline,
      bio: p.Bio,
      location: p.Location,
      instagram: p.Instagram,
      contact_email: p.ContactEmail,
      cover_asset_id: p.CoverAssetID,
      avatar_asset_id: p.AvatarAssetID,
      logo_asset_id: p.LogoAssetID,
      template_id: p.TemplateID,
      accent_color: p.AccentColor,
      background_color: p.BackgroundColor,
      text_color: p.TextColor,
      dark_primary: p.DarkPrimary,
      dark_background: p.DarkBackground,
      dark_text: p.DarkText,
      font_family: p.FontFamily,
      is_published: p.IsPublished,
      social_links: (p.SocialLinks ?? []).map((l) => ({ url: l.url, label: l.label })),
      client_portal_enabled: p.ClientPortalEnabled,
      client_portal_welcome: p.ClientPortalWelcome,
    };
  }

  async function handleSave() {
    if (!portfolio) return;
    setSaving(true);
    setError(null);
    try {
      const next = await api.updateMyPortfolio(buildUpdate(portfolio));
      setPortfolio(next);
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function togglePublish() {
    if (!portfolio) return;
    const next = { ...portfolio, IsPublished: !portfolio.IsPublished };
    setPortfolio(next);
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateMyPortfolio(buildUpdate(next));
      setPortfolio(updated);
    } catch (err) {
      setPortfolio(portfolio); // revert
      setError(err instanceof Error ? err.message : "Publish toggle failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ color: "var(--muted)" }}>Loading…</p>;

  if (!portfolio) {
    return (
      <div>
        <h1 style={{ marginTop: 0 }}>{t("portfolio.title")}</h1>
        <p style={{ color: "var(--muted)" }}>
          {t("portfolio.create_subtitle")}
        </p>
        <form onSubmit={handleCreate} className="card" style={{ maxWidth: 480 }}>
          <div className="form-group">
            <label>{t("portfolio.create_name_label")} *</label>
            <input
              required
              value={createName}
              onChange={(e) => {
                setCreateName(e.target.value);
                if (!createSlugEdited) setCreateSlug(slugify(e.target.value));
              }}
              placeholder="Alice Photography"
            />
          </div>
          <div className="form-group">
            <label>{t("portfolio.create_slug_label")} *</label>
            <input
              required
              value={createSlug}
              onChange={(e) => {
                setCreateSlugEdited(true);
                setCreateSlug(cleanSlugInput(e.target.value));
              }}
              onBlur={(e) => setCreateSlug(slugify(e.target.value))}
              placeholder="alice-photos"
            />
            <small style={{ color: "var(--muted)" }}>
              2–40 chars, lowercase letters / digits / hyphens.
            </small>
          </div>
          {error && <div className="alert alert-error">{error}</div>}
          <button className="btn-primary" type="submit" disabled={creating}>
            {creating ? t("portfolio.creating") : t("portfolio.create_title")}
          </button>
        </form>
      </div>
    );
  }

  const publicHref = runtimeCfg?.subdomain_host
    ? `https://${portfolio.Slug}.${runtimeCfg.subdomain_host}?preview=true`
    : `/${portfolio.Slug}?preview=true`;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1rem" }}>
        <h1 style={{ margin: 0 }}>{t("portfolio.title")}</h1>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <a className="btn-secondary" href={publicHref} target="_blank" rel="noopener noreferrer">
            {t("portfolio.view_site")}
          </a>
          <button
            className={portfolio.IsPublished ? "btn-secondary" : "btn-primary"}
            onClick={togglePublish}
            disabled={saving}
          >
            {portfolio.IsPublished ? t("portfolio.unpublish") : t("portfolio.publish")}
          </button>
        </div>
      </div>
      <p style={{ color: "var(--muted)", marginTop: 0 }}>
        {runtimeCfg?.subdomain_host ? (
          <>
            <a
              href={`https://${portfolio.Slug}.${runtimeCfg.subdomain_host}`}
              target="_blank" rel="noopener noreferrer"
              style={{ fontFamily: "monospace", color: "var(--accent)" }}
            >
              {portfolio.Slug}.{runtimeCfg.subdomain_host}
            </a>
            {" "}·{" "}<span style={{ fontSize: "0.85rem" }}><code>/{portfolio.Slug}</code></span>
          </>
        ) : (
          <><code>/{portfolio.Slug}</code></>
        )}
        {" "}·{" "}
        {portfolio.IsPublished ? <span style={{ color: "var(--primary)" }}>{t("portfolio.live")}</span> : t("portfolio.draft")}
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {savedAt && <div className="alert alert-success">{t("portfolio.saved")}</div>}

      <Section title={t("portfolio.basics")}>
        <Field label={t("portfolio.name_label")} value={portfolio.DisplayName} onChange={(v) => patch("DisplayName", v)} required />
        <Field label={t("portfolio.tagline_label")} value={portfolio.Tagline} onChange={(v) => patch("Tagline", v)} />
        <div>
          <Field label={t("portfolio.headline_label")} value={portfolio.Headline} onChange={(v) => patch("Headline", v)} />
          <AIAssist
            kind="hero_tagline"
            portfolioName={portfolio.DisplayName}
            style={portfolio.Tagline}
            onAccept={(text) => patch("Headline", text)}
            label="Suggest a headline"
          />
        </div>
        <div>
          <TextArea label={t("portfolio.bio_label")} value={portfolio.Bio} onChange={(v) => patch("Bio", v)} rows={6} />
          <AIAssist
            kind="bio"
            portfolioName={portfolio.DisplayName}
            style={portfolio.Tagline}
            keywords={portfolio.Location}
            onAccept={(text) => patch("Bio", text)}
            label="Suggest a bio"
          />
        </div>
        <Field label={t("portfolio.location_label")} value={portfolio.Location} onChange={(v) => patch("Location", v)} placeholder="Amsterdam, NL" />
        <Field label={t("portfolio.email_label")} value={portfolio.ContactEmail} onChange={(v) => patch("ContactEmail", v)} placeholder="hello@example.com" />
        <Field label={t("portfolio.instagram_label")} value={portfolio.Instagram} onChange={(v) => patch("Instagram", v)} placeholder="alice.shoots" />
        <SocialLinksEditor
          links={portfolio.SocialLinks ?? []}
          onChange={(links) => patch("SocialLinks", links)}
          onSave={async (links) => {
            const next = { ...portfolio, SocialLinks: links };
            setSaving(true);
            setError(null);
            try {
              const updated = await api.updateMyPortfolio(buildUpdate(next));
              setPortfolio(updated);
              setSavedAt(Date.now());
            } catch (err) {
              setError(err instanceof Error ? err.message : "Save failed");
            } finally {
              setSaving(false);
            }
          }}
        />
      </Section>

      <Section title={t("portfolio.theme_section")}>
        <div style={{ display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
          <ColorField label="Primary" value={portfolio.AccentColor} onChange={(v) => patch("AccentColor", v)} />
          <ColorField label="Background" value={portfolio.BackgroundColor} onChange={(v) => patch("BackgroundColor", v)} />
          <ColorField label="Text" value={portfolio.TextColor} onChange={(v) => patch("TextColor", v)} />
          <ColorField label="Dark — Primary" value={portfolio.DarkPrimary} onChange={(v) => patch("DarkPrimary", v)} />
          <ColorField label="Dark — Background" value={portfolio.DarkBackground} onChange={(v) => patch("DarkBackground", v)} />
          <ColorField label="Dark — Text" value={portfolio.DarkText} onChange={(v) => patch("DarkText", v)} />
        </div>
      </Section>

      <Section title={t("portfolio.template_section")}>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          {templates.map((t) => (
            <label key={t.ID} style={{ display: "flex", alignItems: "center", gap: ".4rem", padding: ".5rem .75rem", border: `1px solid ${portfolio.TemplateID === t.ID ? "var(--primary)" : "var(--border, #2c3640)"}`, borderRadius: ".4rem", cursor: "pointer" }}>
              <input
                type="radio"
                name="template"
                value={t.ID}
                checked={portfolio.TemplateID === t.ID}
                onChange={() => patch("TemplateID", t.ID)}
                style={{ width: "auto" }}
              />
              <span>{t.Name}</span>
            </label>
          ))}
          {templates.length === 1 && (
            <small style={{ color: "var(--muted)", alignSelf: "center" }}>
              4 more templates land in P2 (magazine, grid, mono, studio, archive).
            </small>
          )}
        </div>
      </Section>

      <Section title={t("portfolio.assets")}>
        <AssetUpload
          label={t("portfolio.logo_label")}
          hint="Wide or square. Shown in the site header next to your name."
          assetKey={portfolio.LogoAssetID}
          previewURL={portfolio.LogoURL}
          type="logo"
          onUploaded={(key) => patch("LogoAssetID", key)}
          onError={(msg) => setError(msg)}
        />
        <AssetUpload
          label={t("portfolio.avatar_label")}
          hint="Round portrait shown on the About page."
          assetKey={portfolio.AvatarAssetID}
          previewURL={portfolio.AvatarURL}
          type="avatar"
          onUploaded={(key) => patch("AvatarAssetID", key)}
          onError={(msg) => setError(msg)}
        />
        <AssetUpload
          label={t("portfolio.cover_label")}
          hint="Banner image on the home page. Also used as the OG card."
          assetKey={portfolio.CoverAssetID}
          previewURL={portfolio.CoverURL}
          type="cover"
          onUploaded={(key) => patch("CoverAssetID", key)}
          onError={(msg) => setError(msg)}
        />
      </Section>

      {/* ── Client Area (collapsible) ── */}
      <button
        type="button"
        onClick={() => setShowClientArea((v) => !v)}
        style={{
          background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: ".4rem",
          color: "var(--muted)", fontSize: ".875rem", padding: 0, marginBottom: ".75rem",
        }}
      >
        <span style={{ transform: showClientArea ? "rotate(90deg)" : "none", display: "inline-block", transition: "transform .15s" }}>▶</span>
        Client Area
      </button>

      {showClientArea && (
        <div className="card" style={{ marginBottom: "1.25rem" }}>
          <h3 style={{ marginTop: 0 }}>Client Area</h3>
          <div className="form-group">
            <label style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
              <input
                type="checkbox"
                checked={portfolio.ClientPortalEnabled ?? false}
                onChange={(e) => patch("ClientPortalEnabled", e.target.checked)}
                style={{ width: "auto" }}
              />
              Enable client portal
            </label>
          </div>
          <div className="form-group">
            <label>Welcome message</label>
            <textarea
              rows={3}
              value={portfolio.ClientPortalWelcome ?? ""}
              onChange={(e) => patch("ClientPortalWelcome", e.target.value)}
            />
            <small style={{ color: "var(--muted)" }}>Shown at the top of your client's overview page</small>
          </div>
        </div>
      )}

      <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        <a className="btn-secondary" href={publicHref} target="_blank" rel="noopener noreferrer">
          Preview site
        </a>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginBottom: "1.25rem" }}>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div className="form-group">
      <label>
        {label}
        {required ? " *" : ""}
      </label>
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, rows }: { label: string; value: string; onChange: (v: string) => void; rows?: number }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows ?? 4} />
    </div>
  );
}

const COLOR_PRESETS = [
  "#0f172a", "#ffffff", "#f8fafc", "#64748b",
  "#10b981", "#6366f1", "#f59e0b", "#ef4444",
  "#ec4899", "#0ea5e9",
];

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const v = value || "#ffffff";
  const norm = v.toLowerCase();
  return (
    <div className="form-group" style={{ marginBottom: 0 }}>
      <label>{label}</label>
      <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap", marginBottom: ".4rem" }}>
        {COLOR_PRESETS.map((c) => {
          const selected = norm === c;
          return (
            <button
              key={c}
              type="button"
              title={c}
              aria-label={c}
              onClick={() => onChange(c)}
              style={{
                width: 26, height: 26, borderRadius: "50%", cursor: "pointer",
                background: c,
                border: selected ? "2px solid var(--accent, #10b981)" : "1px solid var(--border, #2c3640)",
                boxShadow: selected ? "0 0 0 2px rgba(16,185,129,.25)" : "none",
                padding: 0,
              }}
            />
          );
        })}
      </div>
      <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
        <input
          type="color"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          title="Custom color"
          style={{ width: 48, height: 36, padding: 0, border: "1px solid var(--border, #2c3640)", background: "transparent", borderRadius: 4 }}
        />
        <input
          value={v}
          onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, fontFamily: "monospace", fontSize: ".95rem" }}
        />
      </div>
    </div>
  );
}

const PLATFORM_LABELS: Record<string, string> = {
  instagram: "Instagram",
  twitter: "X / Twitter",
  youtube: "YouTube",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  tiktok: "TikTok",
  pinterest: "Pinterest",
  vimeo: "Vimeo",
  behance: "Behance",
  dribbble: "Dribbble",
  web: "Website",
};

function detectPlatform(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    if (host.includes("instagram.com")) return "instagram";
    if (host.includes("twitter.com") || host === "x.com") return "twitter";
    if (host.includes("youtube.com") || host === "youtu.be") return "youtube";
    if (host.includes("facebook.com") || host === "fb.com") return "facebook";
    if (host.includes("linkedin.com")) return "linkedin";
    if (host.includes("tiktok.com")) return "tiktok";
    if (host.includes("pinterest.com")) return "pinterest";
    if (host.includes("vimeo.com")) return "vimeo";
    if (host.includes("behance.net")) return "behance";
    if (host.includes("dribbble.com")) return "dribbble";
  } catch {
    /* ignore */
  }
  return "web";
}

function SocialLinksEditor({
  links,
  onChange,
  onSave,
}: {
  links: SocialLink[];
  onChange: (links: SocialLink[]) => void;
  onSave: (links: SocialLink[]) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [addURL, setAddURL] = useState("");
  const [addLabel, setAddLabel] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  async function handleAdd() {
    const url = addURL.trim();
    if (!url) return;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      setAddError("URL must start with http:// or https://");
      return;
    }
    if (links.length >= 10) {
      setAddError("Maximum 10 links");
      return;
    }
    const newLink: SocialLink = { url, label: addLabel.trim(), platform: detectPlatform(url) };
    const next = [...links, newLink];
    onChange(next);
    setAdding(true);
    setAddError(null);
    try {
      await onSave(next);
      setAddURL("");
      setAddLabel("");
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "Save failed");
      onChange(links); // revert optimistic update
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(i: number) {
    const next = links.filter((_, idx) => idx !== i);
    onChange(next);
    try {
      await onSave(next);
    } catch {
      onChange(links); // revert
    }
  }

  return (
    <div className="form-group">
      <label>{t("portfolio.social_links")}</label>
      {links.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", marginBottom: ".75rem" }}>
          {links.map((link, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: ".5rem", padding: ".4rem .6rem", border: "1px solid var(--border, #2c3640)", borderRadius: ".4rem", fontSize: ".875rem" }}>
              <span style={{ padding: ".1rem .4rem", background: "var(--card-muted, #1e2730)", borderRadius: ".25rem", fontSize: ".75rem", color: "var(--muted)" }}>
                {PLATFORM_LABELS[link.platform] ?? link.platform}
              </span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {link.label || link.url}
              </span>
              <button
                type="button"
                onClick={() => void handleRemove(i)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: "0 .2rem", fontSize: "1rem", lineHeight: 1 }}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {links.length < 10 && (
        <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
          <input
            value={addURL}
            onChange={(e) => { setAddURL(e.target.value); setAddError(null); }}
            placeholder="https://instagram.com/yourhandle"
            style={{ flex: "2 1 200px" }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void handleAdd(); } }}
          />
          <input
            value={addLabel}
            onChange={(e) => setAddLabel(e.target.value)}
            placeholder="Label (optional)"
            style={{ flex: "1 1 120px" }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void handleAdd(); } }}
          />
          <button
            type="button"
            className="btn-secondary"
            disabled={adding || !addURL.trim()}
            onClick={() => void handleAdd()}
            style={{ padding: ".4rem .75rem", fontSize: ".875rem" }}
          >
            {adding ? t("portfolio.adding") : t("portfolio.add_link")}
          </button>
        </div>
      )}
      {addError && <small style={{ color: "var(--error, #f87171)", display: "block", marginTop: ".3rem" }}>{addError}</small>}
      <small style={{ color: "var(--muted)", display: "block", marginTop: ".3rem" }}>
        Up to 10 links. Platform icon is detected automatically from the URL.
      </small>
    </div>
  );
}

function AssetUpload({
  label,
  hint,
  assetKey,
  previewURL,
  type,
  onUploaded,
  onError,
}: {
  label: string;
  hint?: string;
  assetKey: string;
  previewURL?: string;
  type: "logo" | "avatar" | "cover";
  onUploaded: (key: string) => void;
  onError: (msg: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    setUploading(true);
    // Show local preview immediately via object URL
    const objectURL = URL.createObjectURL(file);
    setLocalPreview(objectURL);
    try {
      const { key } = await api.uploadPortfolioAsset(file, type);
      onUploaded(key);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Upload failed");
      setLocalPreview(null);
      URL.revokeObjectURL(objectURL);
    } finally {
      setUploading(false);
    }
  }

  const displayURL = localPreview ?? previewURL;

  return (
    <div className="form-group" style={{ marginBottom: "1rem" }}>
      <label>{label}</label>
      <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" }}>
        {displayURL && (
          <img
            src={displayURL}
            alt={label}
            style={{
              width: type === "cover" ? 160 : 72,
              height: type === "cover" ? 90 : 72,
              objectFit: "cover",
              borderRadius: type === "avatar" ? "50%" : 6,
              border: "1px solid var(--border, #2c3640)",
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <button
            type="button"
            className="btn-secondary"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            style={{ padding: "0.4rem 0.75rem", fontSize: "0.875rem" }}
          >
            {uploading ? "Uploading…" : displayURL ? "Replace" : "Choose file"}
          </button>
          {assetKey && (
            <code style={{ fontSize: "0.75rem", color: "var(--muted)", wordBreak: "break-all" }}>
              {assetKey}
            </code>
          )}
          {hint && <small style={{ color: "var(--muted)" }}>{hint}</small>}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}
